import { useCallback, useMemo, useState } from "react";
import { parsePhoneNumber } from "react-phone-number-input";

// Mirrors the backend contract exactly (zorni-controller PersonPhoneInput):
// indicative: 1-3 digits, no leading zero, never a "+" prefix.
// number: 4-15 digits only.
export const PHONE_INDICATIVE_PATTERN = /^[1-9][0-9]{0,2}$/;
export const PHONE_NUMBER_PATTERN = /^[0-9]{4,15}$/;

export const PHONE_ERROR_CODES = {
  INDICATIVE_REQUIRED: "INDICATIVE_REQUIRED",
  INDICATIVE_INVALID: "INDICATIVE_INVALID",
  NUMBER_REQUIRED: "NUMBER_REQUIRED",
  NUMBER_INVALID: "NUMBER_INVALID",
};

// Default, non-translated messages: used as-is by any caller that doesn't
// map PHONE_ERROR_CODES to its own i18n keys (see ProfilePage.js for the
// react-i18next mapping used on /profil).
const DEFAULT_MESSAGES = {
  [PHONE_ERROR_CODES.INDICATIVE_REQUIRED]: "Country code is required.",
  [PHONE_ERROR_CODES.INDICATIVE_INVALID]:
    'Country code must be 1 to 3 digits, with no leading zero and no "+" sign (e.g. "33").',
  [PHONE_ERROR_CODES.NUMBER_REQUIRED]: "Phone number is required.",
  [PHONE_ERROR_CODES.NUMBER_INVALID]:
    "Phone number must contain 4 to 15 digits only, with no spaces, dashes, or country code.",
};

const makeError = (code) => ({ code, message: DEFAULT_MESSAGES[code] });

export function validatePhoneIndicative(indicative) {
  const value = indicative ?? "";
  if (!value) return makeError(PHONE_ERROR_CODES.INDICATIVE_REQUIRED);
  if (!PHONE_INDICATIVE_PATTERN.test(value)) {
    return makeError(PHONE_ERROR_CODES.INDICATIVE_INVALID);
  }
  return null;
}

export function validatePhoneNumberDigits(number) {
  const value = number ?? "";
  if (!value) return makeError(PHONE_ERROR_CODES.NUMBER_REQUIRED);
  if (!PHONE_NUMBER_PATTERN.test(value)) {
    return makeError(PHONE_ERROR_CODES.NUMBER_INVALID);
  }
  return null;
}

export function validatePhoneParts({ indicative, number } = {}) {
  return {
    indicative: validatePhoneIndicative(indicative),
    number: validatePhoneNumberDigits(number),
  };
}

/**
 * Splits a combined international phone string (as produced by
 * InternationalPhoneInput / react-phone-number-input, e.g. "+212612345678")
 * into normalized, API-ready { indicative, number }.
 *
 * Uses react-phone-number-input's own parser (libphonenumber-js under the
 * hood) for the split rather than a regex: country calling codes are 1 to 3
 * digits depending on the country ("33" for France, "212" for Morocco), so
 * there's no way to find the boundary from the digit string alone without a
 * real calling-code table. The returned `indicative` never carries "+".
 */
export function splitPhoneValue(phoneValue) {
  const raw = (phoneValue ?? "").toString().trim();
  if (!raw) return { indicative: "", number: "" };

  const parsed = parsePhoneNumber(raw);
  if (parsed) {
    return {
      indicative: parsed.countryCallingCode || "",
      number: parsed.nationalNumber || "",
    };
  }

  // Unparseable input (e.g. mid-typing, or missing "+"): best-effort so
  // validation still surfaces a meaningful field error instead of nothing.
  return { indicative: "", number: raw.replace(/[^0-9]/g, "") };
}

/**
 * Reusable phone-number state + validation hook. Binds directly to
 * InternationalPhoneInput's value/onChange (a combined "+<indicative><number>"
 * string) and exposes the normalized, validated { indicative, number } pair
 * ready to send as a Person.phoneNumber input.
 *
 * const phone = usePhoneValidation(person?.phoneNumber);
 * <InternationalPhoneInput value={phone.value} onChange={phone.onChange} />
 * {phone.errors.number && <Text>{t(`phoneValidation.number.${phone.errors.number.code}`, phone.errors.number.message)}</Text>}
 * ...
 * if (!phone.validate()) return; // reveals field errors, blocks submit
 * personInput.phoneNumber = phone.phoneNumber; // null when left empty
 */
// Exported for callers that need to compare a person's persisted phoneNumber
// against the hook's current `value` (e.g. a dirty-check), without
// duplicating the "+<indicative><number>" construction logic.
export function buildPhoneValue(phoneNumber) {
  if (!phoneNumber) return "";
  const { indicative, number } = phoneNumber;
  if (!indicative && !number) return "";
  return `+${indicative || ""}${number || ""}`;
}

export function usePhoneValidation(initialPhoneNumber) {
  const buildValue = buildPhoneValue;

  const [value, setValue] = useState(() => buildValue(initialPhoneNumber));
  const [touched, setTouched] = useState(false);

  const parts = useMemo(() => splitPhoneValue(value), [value]);
  const isEmpty = !value;

  const rawErrors = useMemo(
    () => (isEmpty ? { indicative: null, number: null } : validatePhoneParts(parts)),
    [isEmpty, parts],
  );

  const isValid = isEmpty || (!rawErrors.indicative && !rawErrors.number);

  const onChange = useCallback((newValue) => {
    setTouched(true);
    setValue(newValue || "");
  }, []);

  const reset = useCallback((nextPhoneNumber) => {
    setTouched(false);
    setValue(buildValue(nextPhoneNumber));
  }, []);

  const validate = useCallback(() => {
    setTouched(true);
    return isValid;
  }, [isValid]);

  return {
    value,
    onChange,
    reset,
    validate,
    touched,
    isEmpty,
    isValid,
    errors: touched ? rawErrors : { indicative: null, number: null },
    phoneNumber: isEmpty ? null : { indicative: parts.indicative, number: parts.number },
  };
}
